import {RenewInterval} from 'components/checkout/query/types';
/**
 * Options for usePrices hook.
 * All fields are optional; name defaults to 'DEXA' if not provided.
 */
export type UsePricesOptions = {
	name?: string;
	apptId?: string;
	region?: string;
	bulkQty?: number;
	disc?: string;
};

type Membership = {
	[key in RenewInterval]: Entry[];
}

export type Entry = {
	renewInterval: RenewInterval;
	price: Price;
}

export type Price = {
	quantity: number;
	total: number;
};

export type PriceData = {
	id: string;
	memberships: {
		renewInterval: RenewInterval;
		price: Price;
	}[];
	prices: Price[];
};

// Result type for usePrices (supports all useBulkPrices and usePrices scenarios)
export type PriceResult = {
	id: string;
	shortName: string;
	memberships: Membership;
	oneTime: number[];
	basePrices: { quantity: number; total: number }[];
	discPrices: { quantity: number; total: number }[];
	isLoading: boolean;
	hasError: Error | undefined;
};

// Result of our GQL query for the price hook
export type PriceQueryResult = {
	products: {
		edges: {
			node: {
				id: string;
				shortName: string;
				prices: PriceData;
				base?: {
					prices: { quantity: number; total: number }[];
				};
				disc?: {
					prices: { quantity: number; total: number }[];
				};
			};
		}[];
	};
};

/**
 * Parses the unified price data from the GraphQL response.
 * Returns all fields required by both useBulkPrices and usePrices consumers.
 */
export const parsePriceData = (
	node: {
		id: string;
		shortName: string;
		prices: PriceData;
		base?: { prices: Price[] };
		disc?: { prices: Price[] };
	}
): Omit<PriceResult, 'isLoading' | 'hasError'> => {
	// Parse memberships and oneTime as before
	const {memberships: m, prices} = node.prices || {memberships: [], prices: []};
	const memberships = m
		? (m as Entry[]).reduce<Membership>(
			(acc: Membership, curr: Entry) => {
				const intv = curr.renewInterval;
				if (intv in acc) {
					const entry: Entry = {...curr};
					return {
						...acc,
						[intv]: [...acc[intv], entry],
					};
				}
				return acc;
			},
			{
				[RenewInterval.MONTH]: [],
				[RenewInterval.QUARTER]: [],
			}
		)
		: {[RenewInterval.MONTH]: [], [RenewInterval.QUARTER]: []};

	// Parse base and disc price arrays (for bulk/discounted pricing)
	const basePrices = node.base?.prices || [];
	const discPrices = node.disc?.prices || [];

	// Parse oneTime prices (totals)
	const oneTime = prices ? (prices as Price[]).map((p: Price) => p?.total) : [];

	return {
		id: node.id,
		shortName: node.shortName,
		memberships,
		oneTime,
		basePrices,
		discPrices,
	};
};
